import { useGetOfficeInvoiceReportSettings } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { useGetEDocPlan } from '@/context/services/e_doc_plan/EDocPlan.service';
import {
  SearchInvoiceReportsQueryMfFile,
  SearchInvoiceReportsQueryPaymentStatus,
  SearchInvoiceReportsQueryStatus,
} from '@/context/services/admin_invoice_reports/AdminInvoiceReportsQuery.enums';
import { ADMIN_INVOICE_REPORT_PAYMENT_METHOD } from '@/features/admin_invoice_reports/types';
import { useTranslation } from '@/i18n';
import { useMemo } from 'react';

export const useConfigForm = () => {
  const { t } = useTranslation();
  const { isInvoiceReportDocuments } = useGetEDocPlan();
  const { data: officeSettings } = useGetOfficeInvoiceReportSettings();
  const deptsExisted = officeSettings?.data?.depts_existed;
  const reportDeptsSetting = officeSettings?.data?.report_depts_setting;

  const statusOptions = useMemo(
    () => [
      {
        label: t('admin_invoice_reports.search.status.unsubmitted'),
        value: SearchInvoiceReportsQueryStatus.unsubmitted,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_all'),
        value: SearchInvoiceReportsQueryStatus.waiting_all,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_one'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_one,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_two'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_two,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_three'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_three,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_four'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_four,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_five'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_five,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_six'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_six,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_seven'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_seven,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_eight'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_eight,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_nine'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_nine,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_ten'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_ten,
      },
      {
        label: t('admin_invoice_reports.search.status.approved'),
        value: SearchInvoiceReportsQueryStatus.approved,
      },
      {
        label: t('admin_invoice_reports.search.status.canceled'),
        value: SearchInvoiceReportsQueryStatus.canceled,
      },
      {
        label: t('admin_invoice_reports.search.status.rejected'),
        value: SearchInvoiceReportsQueryStatus.rejected,
      },
    ],
    [t]
  );

  const paymentStatusOptions = useMemo(
    () => [
      {
        label: t('admin_invoice_reports.search.payment_status.not_yet_paid'),
        value: SearchInvoiceReportsQueryPaymentStatus.not_yet_paid,
        tooltip: t(
          'admin_invoice_reports.search.payment_status.not_yet_paid_tooltip'
        ),
      },
      {
        label: t('admin_invoice_reports.search.payment_status.paid'),
        value: SearchInvoiceReportsQueryPaymentStatus.paid,
        tooltip: t('admin_invoice_reports.search.payment_status.paid_tooltip'),
      },
    ],
    [t]
  );

  const mfFileOptions = useMemo(() => {
    const base = [
      {
        label: t('admin_invoice_reports.search.mf_file.not_attached'),
        value: SearchInvoiceReportsQueryMfFile.not_attached,
      },
      {
        label: t('admin_invoice_reports.search.mf_file.attached'),
        value: SearchInvoiceReportsQueryMfFile.attached,
      },
    ];
    if (isInvoiceReportDocuments) {
      return [
        ...base,
        {
          label: t(
            'admin_invoice_reports.search.mf_file.attached_receipt_type_paper'
          ),
          value: SearchInvoiceReportsQueryMfFile.attached_receipt_type_paper,
        },
        {
          label: t(
            'admin_invoice_reports.search.mf_file.attached_receipt_type_e_doc'
          ),
          value: SearchInvoiceReportsQueryMfFile.attached_receipt_type_e_doc,
        },
      ];
    }
    return base;
  }, [t, isInvoiceReportDocuments]);

  const paymentMethodOptions = useMemo(
    () => [
      {
        label: t('admin_invoice_reports.search.payment_method.bank_transfer'),
        value: ADMIN_INVOICE_REPORT_PAYMENT_METHOD.bank_transfer,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.cash'),
        value: ADMIN_INVOICE_REPORT_PAYMENT_METHOD.cash,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.payment_slip'),
        value: ADMIN_INVOICE_REPORT_PAYMENT_METHOD.payment_slip,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.automatic_debit'),
        value: ADMIN_INVOICE_REPORT_PAYMENT_METHOD.automatic_debit,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.credit_card'),
        value: ADMIN_INVOICE_REPORT_PAYMENT_METHOD.credit_card,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.prepaid_card'),
        value: ADMIN_INVOICE_REPORT_PAYMENT_METHOD.prepaid_card,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.check'),
        value: ADMIN_INVOICE_REPORT_PAYMENT_METHOD.check,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.promissory_note'),
        value: ADMIN_INVOICE_REPORT_PAYMENT_METHOD.promissory_note,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.electronic_bill'),
        value: ADMIN_INVOICE_REPORT_PAYMENT_METHOD.electronic_bill,
      },
      {
        label: t(
          'admin_invoice_reports.search.payment_method.overseas_remittance'
        ),
        value: ADMIN_INVOICE_REPORT_PAYMENT_METHOD.overseas_remittance,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.others'),
        value: ADMIN_INVOICE_REPORT_PAYMENT_METHOD.others,
      },
    ],
    [t]
  );

  const labels = useMemo(
    () => ({
      query: {
        text: t('admin_invoice_reports.search.keyword'),
        tooltip: t('admin_invoice_reports.search.keyword_tooltip'),
      },
      number: { text: t('admin_invoice_reports.search.number') },
      status: { text: t('admin_invoice_reports.search.status') },
      applicant: { text: t('admin_invoice_reports.search.applicant') },
      applicant_department: {
        text: t('admin_invoice_reports.search.applicant_department'),
      },
      department: { text: t('admin_invoice_reports.search.department') },
      counterparty: { text: t('admin_invoice_reports.search.counterparty') },
      ap_payee: { text: t('admin_invoice_reports.search.ap_payee') },
      payment_method: {
        text: t('admin_invoice_reports.search.payment_method'),
      },
      total_amount: { text: t('admin_invoice_reports.search.total_amount') },
      payment_status: {
        text: t('admin_invoice_reports.search.payment_status'),
      },
      mf_file: { text: t('admin_invoice_reports.search.mf_file') },
      created_date: { text: t('admin_invoice_reports.search.created_date') },
      submitted_date: {
        text: t('admin_invoice_reports.search.submitted_date'),
      },
      approved_date: {
        text: t('admin_invoice_reports.search.approved_date'),
      },
      book_date: { text: t('admin_invoice_reports.search.book_date') },
      due_date: { text: t('admin_invoice_reports.search.due_date') },
      payment_date: { text: t('admin_invoice_reports.search.payment_date') },
      reset_button: t('admin_invoice_reports.search.reset_button'),
      search_button: t('admin_invoice_reports.search.search_button'),
    }),
    [t]
  );

  const selectProps = useMemo(
    () => ({
      size: 'small' as const,
      apPayee: {
        inputPlaceholder: t(
          'admin_invoice_reports.search.ap_payee.placeholder'
        ),
        size: 'small' as const,
      },
      applicant: {
        placeholder: t('admin_invoice_reports.search.applicant.placeholder'),
        size: 'small' as const,
        mode: 'multiple' as const,
      },
      applicantDepartment: {
        placeholder: t(
          'admin_invoice_reports.search.applicant_department.placeholder'
        ),
        size: 'small' as const,
        mode: 'multiple' as const,
      },
      department: {
        placeholder: t('admin_invoice_reports.search.department.placeholder'),
        size: 'small' as const,
        mode: 'multiple' as const,
      },
      counterparty: {
        placeholder: t('admin_invoice_reports.search.counterparty.placeholder'),
        size: 'small' as const,
        mode: 'multiple' as const,
      },
    }),
    [t]
  );

  const inputProps = useMemo(
    () => ({
      query: { search: true, size: 'small' as const },
      number: { search: false, size: 'small' as const },
    }),
    []
  );

  return {
    labels,
    selectProps,
    inputProps,
    statusOptions,
    paymentStatusOptions,
    mfFileOptions,
    paymentMethodOptions,
    deptsExisted,
    reportDeptsSetting,
  };
};
